/**
 * src/ai/AiAssistantContext.jsx
 *
 * Per-user preference for showing/hiding the AI assistant widget.
 * Source of truth: GET/PUT /api/ai/preferences (stored in predetermined_values).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import config from '../config.json'

const AiAssistantContext = createContext(null)

function getPreferencesUrl() {
  if (!config.apiUrl) return null
  return `${config.apiUrl}/ai/preferences`
}

export function AiAssistantProvider({ children }) {
  const [enabled, setEnabledState] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const url = getPreferencesUrl()
    if (!url || !config.token) {
      setLoaded(true)
      return
    }

    fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', token: config.token },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === 'ok' && typeof data.body?.enabled === 'boolean') {
          setEnabledState(data.body.enabled)
        }
      })
      .catch((err) => {
        console.error('AiAssistantContext: failed to load preference', err)
      })
      .finally(() => setLoaded(true))
  }, [])

  const setEnabled = useCallback(async (nextEnabled) => {
    const url = getPreferencesUrl()
    if (!url || !config.token) {
      setEnabledState(nextEnabled)
      return { ok: true }
    }

    setSaving(true)
    try {
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', token: config.token },
        body: JSON.stringify({ enabled: nextEnabled }),
      })
      const data = await res.json()

      if (data.status !== 'ok') {
        return { ok: false, msg: data.msg || 'No se pudo guardar la preferencia del asistente.' }
      }

      const savedEnabled = typeof data.body?.enabled === 'boolean' ? data.body.enabled : nextEnabled
      setEnabledState(savedEnabled)
      return { ok: true }
    } catch (err) {
      console.error('AiAssistantContext: failed to save preference', err)
      return { ok: false, msg: config.fetchErrorMsg || 'Error de conexión.' }
    } finally {
      setSaving(false)
    }
  }, [])

  const value = useMemo(
    () => ({ enabled, loaded, saving, setEnabled }),
    [enabled, loaded, saving, setEnabled],
  )

  return (
    <AiAssistantContext.Provider value={value}>
      {children}
    </AiAssistantContext.Provider>
  )
}

export function useAiAssistant() {
  const ctx = useContext(AiAssistantContext)
  if (!ctx) {
    throw new Error('useAiAssistant must be used within AiAssistantProvider')
  }
  return ctx
}

export default AiAssistantContext
